import { z } from "zod";
import { productionReportQuery } from "../lib/nav-query.js";

// ⚠️ OVERRIDE de `max-lines` (eslint.config.mjs) — registry de DADOS puros.
//
// Uma entrada por TELA: papel mínimo, args e a rota. Ele cresce por definição a cada tela nova, e
// a única lógica que já apareceu aqui (a query string) mora em `lib/nav-query.ts` desde a Spec 083 —
// é o mesmo caminho que qualquer `build` não-trivial deve seguir.
//
// Quebrá-lo por domínio destruiria o que ele tem de útil: a BIJEÇÃO com os comandos `navigate` do
// catálogo é verificada contra este objeto, e espalhá-la em arquivos faria "onde entra a tela nova?"
// virar uma pergunta — que é exatamente como uma entrada acaba em dois lugares, ou em nenhum.

// NAV_REGISTRY: fonte ÚNICA da família de navegação. Cada entrada define o papel
// mínimo, o schema de args e como MONTAR a rota do app. O catálogo de comandos (kind `navigate`) é
// DERIVADO daqui (assistant-catalog-navigate.ts) e o self-check exige bijeção — assim o
// executor web, o catálogo e as rotas nunca divergem. `build` retorna caminho relativo (Vite proxy).

const Uuid = z.string().uuid();
const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use uma data no formato AAAA-MM-DD.");

export interface NavSpec {
  minRole: "OPERADOR" | "GESTOR";
  argsSchema: z.ZodTypeAny; // sempre .strict()
  build: (args: Record<string, string>) => string;
  description: string;
}

// Convenção das descrições (para o modelo distinguir de `display`): NAVEGAÇÃO = "Abre a tela … /
// leva o usuário para a página". Use estes comandos quando o usuário pede para ABRIR/IR PARA/MOSTRAR
// A TELA de algo. Os comandos `display` (Exibição) apenas RESPONDEM no chat e NÃO trocam de tela.
export const NAV_REGISTRY: Record<string, NavSpec> = {
  "nav.dashboard": {
    minRole: "OPERADOR",
    argsSchema: z.object({}).strict(),
    build: () => "/",
    description: "Abre a tela do painel geral (navega o usuário direto para a página).",
  },
  "nav.lines": {
    minRole: "OPERADOR",
    argsSchema: z.object({}).strict(),
    build: () => "/lines",
    description: "Abre a tela com a lista de linhas de produção (navega direto).",
  },
  "nav.lineDetail": {
    minRole: "OPERADOR",
    argsSchema: z.object({ lineId: Uuid }).strict(),
    build: (a) => `/lines/${a.lineId}/detail`,
    description:
      "Abre a tela de detalhe de uma linha — status, máquinas e eventos (navega direto). Use este quando o usuário pede 'abra/mostre a tela da linha X'.",
  },
  "nav.machineDetail": {
    minRole: "OPERADOR",
    argsSchema: z.object({ lineId: Uuid, machineId: Uuid }).strict(),
    build: (a) => `/lines/${a.lineId}/detail?machine=${a.machineId}`,
    description:
      "Abre a tela de detalhe da linha já com uma máquina selecionada (não há rota própria de máquina).",
  },
  "nav.lineEdit": {
    minRole: "GESTOR",
    argsSchema: z.object({ lineId: Uuid }).strict(),
    build: (a) => `/lines/${a.lineId}/edit`,
    description: "Abre a tela de edição de uma linha (navega direto; só GESTOR).",
  },
  "nav.products": {
    minRole: "OPERADOR",
    argsSchema: z.object({}).strict(),
    build: () => "/products",
    description: "Abre a tela de produtos (navega direto).",
  },
  "nav.orders": {
    minRole: "OPERADOR",
    argsSchema: z.object({}).strict(),
    build: () => "/orders",
    description: "Abre a tela de ordens de produção (navega direto).",
  },
  "nav.alarms": {
    minRole: "OPERADOR",
    argsSchema: z.object({}).strict(),
    build: () => "/alarms",
    description: "Abre a tela de alarmes (navega direto).",
  },
  "nav.devices": {
    minRole: "GESTOR",
    argsSchema: z.object({}).strict(),
    build: () => "/devices",
    description: "Abre a tela de dispositivos/CLP (navega direto; só GESTOR).",
  },
  "nav.simulations": {
    minRole: "OPERADOR",
    argsSchema: z.object({}).strict(),
    build: () => "/simulations",
    description: "Abre a tela com a lista de simulações/cenários (navega direto).",
  },
  "nav.sandbox": {
    minRole: "GESTOR",
    argsSchema: z.object({ scenarioId: Uuid }).strict(),
    build: (a) => `/simulations/sandbox/${a.scenarioId}`,
    description: "Abre a tela do sandbox de um cenário (navega direto; só GESTOR).",
  },
  "nav.simulationDetail": {
    minRole: "OPERADOR",
    argsSchema: z.object({ simulationId: Uuid }).strict(),
    build: (a) => `/simulations/${a.simulationId}`,
    description: "Abre a tela de resultado de uma simulação (navega direto).",
  },
  "nav.autoBatch": {
    minRole: "OPERADOR",
    argsSchema: z.object({ batchId: Uuid }).strict(),
    build: (a) => `/simulations/auto/${a.batchId}`,
    description: "Abre a tela de um lote de sugestões automáticas (navega direto).",
  },
  "nav.interpretation": {
    minRole: "GESTOR",
    argsSchema: z.object({ simulationId: Uuid, interpretationId: Uuid }).strict(),
    build: (a) => `/simulations/${a.simulationId}/interpretations/${a.interpretationId}`,
    description:
      "Abre a tela de uma interpretação de simulação já criada (navega direto; só GESTOR).",
  },
  // Telas que faltavam navegação. `nav.simulationCompare` usa query params a/b
  // (a tela lê useSearchParams). Papéis espelham o gating das telas (users/audit/lineNew/interpretações = GESTOR).
  "nav.users": {
    minRole: "GESTOR",
    argsSchema: z.object({}).strict(),
    build: () => "/users",
    description: "Abre a tela de administração de usuários (navega direto; só GESTOR).",
  },
  // Spec 077 — as duas filas do ciclo fechado. GESTOR, como a entrada de `NAV_ITEMS`: julgar por
  // que divergiu e encerrar calibração são decisões de PCP.
  "nav.closedLoop": {
    minRole: "GESTOR",
    argsSchema: z.object({}).strict(),
    build: () => "/ciclo-fechado",
    description:
      "Abre a tela do ciclo fechado — apurações aguardando classificação e ações de calibração (navega direto; só GESTOR).",
  },
  "nav.audit": {
    minRole: "GESTOR",
    argsSchema: z.object({}).strict(),
    build: () => "/audit",
    description: "Abre a tela de auditoria — trilha de ações (navega direto; só GESTOR).",
  },
  "nav.dataEntry": {
    minRole: "OPERADOR",
    argsSchema: z.object({}).strict(),
    build: () => "/data-entry",
    description: "Abre a tela de lançamento manual de dados (navega direto).",
  },
  "nav.lineNew": {
    minRole: "GESTOR",
    argsSchema: z.object({}).strict(),
    build: () => "/lines/new",
    description:
      "Abre o editor de criação de uma nova linha de produção (navega direto; só GESTOR).",
  },
  "nav.simulationCompare": {
    minRole: "OPERADOR",
    argsSchema: z.object({ a: Uuid, b: Uuid }).strict(),
    build: (x) => `/simulations/compare?a=${x.a}&b=${x.b}`,
    description: "Abre a comparação lado-a-lado de duas simulações (navega direto).",
  },
  // A tela onde o expediente da FÁBRICA é declarado. GESTOR, espelhando o
  // `gestorOnly` do `NAV_ITEMS`: navegar para uma tela que o papel não abre é prometer o que a
  // aplicação recusa. Sem comando de escrita nesta fatia (plan.md, MCP Impact) — configuração de
  // calendário tem args estruturados pesados, e abrir a tela resolve o caso de uso.
  "nav.organization": {
    minRole: "GESTOR",
    // Spec 084 — `section` leva à âncora da seção. Enum, e não texto livre: uma âncora inventada
    // abriria a tela no topo em silêncio, e o gestor concluiria que a seção não existe.
    argsSchema: z.object({ section: z.enum(["caracteristicas"]).optional() }).strict(),
    build: (a) => (a.section ? `/organizacao#${a.section}` : "/organizacao"),
    description:
      "Abre a tela da organização — nome, fuso horário e o expediente padrão da fábrica, com feriados e regras recorrentes. Com section=caracteristicas, abre direto na seção de características de batelada (navega direto; só GESTOR).",
  },
  // Os COMPROMISSOS de uma linha: cenários aplicados, vigência e apuração.
  // GESTOR, espelhando o `RequireRole` da rota: navegar para uma tela que o papel não abre é
  // prometer o que a aplicação recusa. Sem comando de ESCRITA nesta fatia (plan.md, MCP Impact) —
  // aplicar um cenário compromete a fábrica com uma janela real, e a feature existe justamente
  // para tornar esse ato deliberado; fazê-lo por conversa seria o oposto.
  "nav.appliedScenarios": {
    minRole: "GESTOR",
    argsSchema: z.object({ lineId: Uuid }).strict(),
    build: (a) => `/lines/${a.lineId}/applied-scenarios`,
    description:
      "Abre a tela dos cenários aplicados de uma linha — os compromissos assumidos, a vigência de cada um e a apuração do que realmente aconteceu (navega direto; só GESTOR).",
  },
  // Spec 078 (T126) — a cena 3D da linha, a biblioteca de modelos e a rota ambiente (TV). OPERADOR
  // nas três: a tela da linha e a biblioteca já são de todos, e a rota ambiente é leitura pura.
  // Sem comando de ESCRITA da cena (colocação, calibração, links de exibição): mover uma máquina
  // por conversa é editar o chão de fábrica sem ver o resultado, e gerar link de TV é dar acesso
  // sem login — ambos ficam deliberadamente na tela (plan.md, MCP Impact).
  "nav.lineScene": {
    minRole: "OPERADOR",
    argsSchema: z.object({ lineId: Uuid }).strict(),
    build: (a) => `/lines/${a.lineId}/detail?view=3d`,
    description:
      "Abre a cena 3D da linha — as máquinas como estão no chão de fábrica, com status, vazão e alertas de calor ao vivo (navega direto).",
  },
  "nav.modelsLibrary": {
    minRole: "OPERADOR",
    argsSchema: z.object({}).strict(),
    build: () => "/modelos-3d",
    description:
      "Abre a biblioteca de modelos 3D — os arquivos .glb enviados, a calibração de cada um e as máquinas que os usam (navega direto).",
  },
  "nav.lineAmbient": {
    minRole: "OPERADOR",
    argsSchema: z.object({ lineId: Uuid }).strict(),
    build: (a) => `/lines/${a.lineId}/ambient`,
    description:
      "Abre a rota ambiente da linha — a cena 3D em tela cheia para TV, sem menus, com contagens e relógio (navega direto).",
  },
  "nav.interpretationsList": {
    minRole: "GESTOR",
    argsSchema: z.object({ simulationId: Uuid }).strict(),
    build: (a) => `/simulations/${a.simulationId}/interpretations`,
    description: "Abre a lista de interpretações de uma simulação (navega direto; só GESTOR).",
  },
  // Spec 083 — o controle de produção. `OPERADOR` espelha o gating da TELA, que é de leitura para
  // qualquer autenticado: quem aponta produção é justamente quem mais olha o número do turno.
  //
  // A aba do detalhe da linha NÃO ganha entrada própria — é a mesma tela com o recorte travado, e
  // `nav.lineDetail` já a alcança. Duas entradas para uma tela fariam o modelo escolher entre elas
  // sem critério.
  "nav.productionReport": {
    minRole: "OPERADOR",
    argsSchema: z
      .object({
        lineId: Uuid,
        scope: z.enum(["line", "chain"]).optional(),
        from: DateOnly.optional(),
        to: DateOnly.optional(),
        groupBy: z.enum(["day", "week", "month"]).optional(),
        // Spec 084 — o recorte e o quadro viajam por ID, não por nome: a tela lê a URL e não tem
        // como resolver um nome. Quem traduz o nome que o gestor falou é a tool de leitura, e é
        // dela que o agente tira estes ids.
        characteristicId: Uuid.optional(),
        value: z.string().min(1).max(210).optional(),
        compareBy: Uuid.optional(),
      })
      .strict(),
    build: (a) => `/controle-de-producao?${productionReportQuery(a)}`,
    description:
      "Abre a tela de controle de produção de uma linha ou da cadeia dela, no período pedido — saída por destino, refugo e indicadores. Aceita o recorte por característica de batelada (characteristicId + value) e o quadro comparativo (compareBy), navega direto.",
  },
  // Spec 084 — as BATELADAS da linha: código, marca e origem, no cartão do detalhe da linha.
  // Âncora, e não rota própria: o cartão vive na aba padrão do detalhe, e a aba é estado local.
  "nav.lineBatches": {
    minRole: "OPERADOR",
    argsSchema: z.object({ lineId: Uuid }).strict(),
    build: (a) => `/lines/${a.lineId}/detail#bateladas`,
    description:
      "Abre o detalhe da linha já no cartão de bateladas — código, características e origem de cada uma, com o que está pendente de marcação (navega direto).",
  },
};

export function getNavSpec(name: string): NavSpec | undefined {
  return NAV_REGISTRY[name];
}
